import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatINRExact } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { Button } from "../components/ui/button";
import { ShoppingBag, AlertTriangle, CheckCircle2 } from "lucide-react";

/** Landing page for the abandoned-cart email link. Restores the saved cart
 *  and sends the shopper to checkout, one click from paying. */
export default function RecoverCart() {
    const { token } = useParams();
    const nav = useNavigate();
    const { addItem, clear } = useCart();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [restored, setRestored] = useState(false);

    useEffect(() => {
        api.get(`/cart/recover/${token}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "This link is no longer valid."));
    }, [token]);

    const restore = () => {
        clear();
        (data.items || []).forEach((i) => {
            addItem(
                { id: i.product_id, name: i.product_name, slug: "", images: i.image ? [i.image] : [] },
                { size_id: i.size_id, size_label: i.size_label, price: i.price },
                i.quantity,
            );
        });
        setRestored(true);
        setTimeout(() => nav("/checkout"), 600);
    };

    if (error) {
        return (
            <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="recover-error">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-stone-900">Link unavailable</h1>
                <p className="text-sm text-stone-600 mt-3">{error}</p>
                <Link to="/shop" className="text-sm text-sky-700 hover:underline mt-6 inline-block">Browse medicines</Link>
            </div>
        );
    }
    if (!data) return <div className="max-w-lg mx-auto px-4 py-24 text-center text-stone-500">Fetching your cart…</div>;

    if (data.already_ordered) {
        return (
            <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="recover-already-ordered">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-stone-900">Already ordered</h1>
                <p className="text-sm text-stone-600 mt-3">This cart has already been placed as an order. Nothing more to do.</p>
                <Link to="/orders" className="text-sm text-sky-700 hover:underline mt-6 inline-block">View my orders</Link>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 py-12" data-testid="recover-cart-page">
            <div className="label-eyebrow text-sky-700">Welcome back</div>
            <h1 className="font-serif text-4xl text-stone-900 mt-2">
                {data.name ? `${data.name.split(" ")[0]}, your cart is saved` : "Your cart is saved"}
            </h1>
            <p className="text-sm text-stone-600 mt-2">
                Exactly as you left it. Restore it and you're one step from checkout.
            </p>

            <div className="bg-white border border-stone-200 mt-6">
                <div className="p-5 space-y-2">
                    {(data.items || []).map((it, i) => (
                        <div key={i} className="flex justify-between gap-4 text-sm border-b border-stone-100 pb-2 last:border-0" data-testid={`recover-item-${i}`}>
                            <span className="text-stone-700">
                                {it.product_name} <span className="text-stone-400">{it.size_label} × {it.quantity}</span>
                            </span>
                            <span className="font-mono">{formatINRExact(it.price * it.quantity)}</span>
                        </div>
                    ))}
                </div>
                <div className="px-5 py-4 bg-stone-50/60 border-t border-stone-200 flex justify-between font-serif text-2xl">
                    <span>Cart total</span>
                    <span data-testid="recover-total">{formatINRExact(data.total)}</span>
                </div>
            </div>

            <Button
                onClick={restore}
                disabled={restored}
                className="rounded-none w-full mt-6 py-6 bg-sky-700 hover:bg-sky-800 text-stone-50"
                data-testid="recover-restore-btn"
            >
                <ShoppingBag className="w-4 h-4 mr-2" />
                {restored ? "Taking you to checkout…" : "Restore cart & checkout"}
            </Button>
            <p className="text-[11px] text-stone-500 mt-3 text-center">
                Paying online at checkout applies an automatic prepaid discount.
            </p>
        </div>
    );
}
